/** ANALYTICS — attribution, scenarios, behaviour, the stress catalogue. */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
  GET_SECURITY_GROUPS, GET_BENCHMARKS, RETURN_ATTRIBUTION, RETURN_ATTRIBUTION_WITH_INDEX, BRINSON_ATTRIBUTION,
  LATEST_BRINSON_ATTRIBUTION, ATTRIBUTION_SETTINGS, ANALYZE_SCENARIO,
  MF_MULTIFACTOR_ATTRIBUTION, PORTFOLIO_MULTIFACTOR_ATTRIBUTION,
  BEHAVIORAL_RISK, STRESS_SCENARIOS, SENSITIVITY_SCENARIOS, RISK_SIMULATION_CONFIGURATIONS,
} from '../queries';
import { EMPTY } from './_shared';
import { num } from '../../lib/format';
import { periodWindows } from '../../lib/cadence';

/* The one summary row an older attribution run stored in place of the index
   names a fund does not own. The resolver no longer serves it — those names
   arrive one per row, `held: false`, when `includeNotHeld` is asked for — but a
   series stored before that is skipped here rather than read as a position. */
export const NOT_HELD = 'NOT_HELD';

/** Resolves a bucketing SecurityGroup by name ("Major Sectors", "Asset Allocation"). */

export function useSecurityGroup(nameMatch) {
  const { data, loading } = useQuery(GET_SECURITY_GROUPS, { fetchPolicy: 'cache-first' });
  const groups = data?.getSecurityGroups ?? EMPTY;
  const match = groups.find((g) => new RegExp(nameMatch, 'i').test(g.name || ''));
  return { loading, groups, group: match ?? null, groupId: match?.id ?? null };
}

/**
 * Brinson attribution.
 *
 * Units, which the resolver does not advertise: `portWeight` / `benchWeight`
 * are fractions (0.0273 = 2.73%), while `portReturn`, `benchReturn`,
 * `activeReturn` and the three effects are **already percentages**
 * (1.2541 = 1.25%). Multiplying those by 100 again is how a 1.25% month
 * becomes 125%.
 *
 * Periods must be combined the right way too: returns compound, effects add.
 * Summing 1,245 periods of returns produced a headline of +47,759%.
 *
 * The window defaults to a trailing year — attribution since inception over
 * every stored period is rarely the question being asked.
 */
export function useAttribution({
  portfolioSymbol, groupName = 'sector', benchmarkSymbol,
  frequency = 'monthly', months = 12,
  /* An explicit window — a cadence's to-date range — overrides the trailing
     `months`. `latestOnly` keeps just the newest period the response carries,
     which is how "the latest day" is asked for without knowing which day the
     nightly run last stored. */
  startDate: fixedStart, endDate: fixedEnd, latestOnly = false,
  /* A bucketing chosen by id — the deep-dive's grouping picker — wins over the
     name match, which is only the default. */
  groupId: fixedGroupId = null,
  /* Serve the benchmark names the fund does not own, by name, inside each
     bucket — the deep-dive's underweights. Needs the backend argument; a
     backend without it falls back to the held-only drill-down. */
  includeNotHeld = false,
}) {
  const { asOf } = useSelector((s) => s.ui);
  const { groupId: matchedGroupId, loading: groupLoading } = useSecurityGroup(groupName);
  const groupId = fixedGroupId ?? matchedGroupId;

  const end = fixedEnd || asOf || new Date().toISOString().slice(0, 10);
  let startDate = fixedStart;
  if (!startDate) {
    const from = new Date(end);
    from.setMonth(from.getMonth() - months);
    startDate = from.toISOString().slice(0, 10);
  }

  const variables = {
    portfolioSymbol,
    securityGroupId: groupId,
    benchmarkSymbol: benchmarkSymbol ?? null,
    startDate,
    endDate: end,
    frequency,
  };
  const off = !portfolioSymbol || !groupId;
  const withIndex = useQuery(RETURN_ATTRIBUTION_WITH_INDEX, {
    variables,
    skip: off || !includeNotHeld,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /* A backend that predates `includeNotHeld` rejects the whole document at
     validation; read the plain drill-down then rather than blank the panel. */
  const unsupported = Boolean(withIndex.error?.graphQLErrors?.some(
    (e) => /includeNotHeld|securityName|Unknown argument|Cannot query field/i.test(e.message || ''),
  ));
  const plain = useQuery(RETURN_ATTRIBUTION, {
    variables,
    skip: off || (includeNotHeld && !unsupported),
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const { data, loading, error } = includeNotHeld && !unsupported ? withIndex : plain;

  const res = data?.getReturnAttribution ?? null;
  return useMemo(() => {
    const allRows = res?.rows ?? EMPTY;
    const allTotals = res?.periodTotals ?? EMPTY;
    /* Period labels sort as dates ("2026-09-14", "2026-09"), so the greatest is
       the newest. */
    const latest = latestOnly
      ? allTotals.reduce((mx, r) => (String(r.period) > mx ? String(r.period) : mx), '') || null
      : null;
    const rows = latest ? allRows.filter((r) => String(r.period) === latest) : allRows;
    const totals = latest ? allTotals.filter((r) => String(r.period) === latest) : allTotals;
    const benchTotalByPeriod = new Map(totals.map((t) => [String(t.period), Number(t.benchReturn) || 0]));
    /* Whether the drill-down carries benchmark weights at all. A backend that
       drops them on its stored-row read sends 0 for every stock, and a split
       built on a zero benchmark weight is a wrong number, not a small one — so
       without them only the stock's total effect is shown. */
    const benchKnown = rows.some((r) => (r.securities ?? EMPTY).some((sec) => Number(sec.benchWeight) > 0));
    /* Roll the per-period rows up to one line per bucket. */
    const byBucket = new Map();
    rows.forEach((r) => {
      const key = r.sector || 'Unclassified';
      const cur = byBucket.get(key) || {
        label: key, allocation: 0, selection: 0, total: 0, portWeight: 0, benchWeight: 0,
        holdings: new Map(),
      };
      cur.allocation += Number(r.allocationEffect) || 0;
      cur.selection += Number(r.selectionEffect) || 0;
      cur.total += Number(r.totalEffect) || 0;
      cur.portWeight = Number(r.portWeight) || cur.portWeight;
      cur.benchWeight = Number(r.benchWeight) || cur.benchWeight;
      /* The stocks inside the bucket, rolled up the same way as the bucket:
         contributions add across periods, returns compound, and a weight is a
         point-in-time share so the latest period's stands. This is what a
         bucket row expands into. */
      /* The bucket's stocks, split the way the bucket is so every column adds
         up to it. The resolver measures each stock against the WHOLE benchmark
         (its `totalContribution` = active weight × (stock return − index
         return)), which gives one number per stock but no split. Nested inside
         the sector, the same number divides exactly:

           allocation = active weight × (sector's index return − index return)
           selection  = total − allocation   (= active weight × (stock − sector))

         Summed over every name in the sector — held and not — these are the
         sector's own allocation, selection and total effects, so the sum only
         closes when the names the fund does not own are in it. `includeNotHeld`
         asks the resolver for them, one row each with `held: false`; a backend
         without that argument serves the held names alone, and what they leave
         unexplained is taken as the sector less its held stocks below. */
      const indexReturn = benchTotalByPeriod.get(String(r.period)) ?? 0;
      const sectorIndexReturn = Number(r.benchReturn) || 0;
      (r.securities ?? EMPTY).forEach((sec) => {
        if (!sec?.securitySymbol || sec.securitySymbol === NOT_HELD) return;
        const h = cur.holdings.get(sec.securitySymbol) || {
          symbol: sec.securitySymbol,
          name: null,
          /* False for a benchmark name the fund does not own (served only when
             asked for); true if the fund held it in any period. */
          held: false,
          weight: 0, benchWeight: 0, contribution: 0, allocation: 0, selection: 0, active: 0, growth: 1,
        };
        if (sec.securityName) h.name = sec.securityName;
        if (sec.held !== false) h.held = true;
        const activeWeight = (Number(sec.portWeight) || 0) - (Number(sec.benchWeight) || 0);
        /* The stock's active effect is allocation + selection by definition.
           Read as that sum rather than as `totalContribution`, which a backend
           that does not carry it through its stored-row read sends as 0 while
           the allocation still arrives. */
        const total = (Number(sec.allocationContribution) || 0) + (Number(sec.selectionContribution) || 0);
        h.contribution += Number(sec.contribution) || 0;
        h.active += total;
        if (benchKnown) {
          const allocation = activeWeight * (sectorIndexReturn - indexReturn);
          h.allocation += allocation;
          h.selection += total - allocation;
        }
        h.weight = Number(sec.portWeight) || h.weight;
        h.benchWeight = Number(sec.benchWeight) || h.benchWeight;
        h.growth *= 1 + (Number(sec.portReturn) || 0) / 100;
        cur.holdings.set(sec.securitySymbol, h);
      });
      byBucket.set(key, cur);
    });
    const buckets = [...byBucket.values()]
      .map(({ holdings, ...b }) => {
        const held = [...holdings.values()]
          .map(({ growth, ...h }) => ({
            ...h,
            portReturn: (growth - 1) * 100,
            /* Unknown, not zero, when the benchmark side was not sent. */
            ...(benchKnown ? {} : { allocation: null, selection: null, benchWeight: null }),
          }))
          /* Biggest effect either way first, so the preview of a bucket shows its
             largest drags as well as its largest helpers. */
          .sort((x, y) => Math.abs(y.active) - Math.abs(x.active));
        const sum = (k) => held.reduce((t, h) => t + h[k], 0);
        /* Whatever the bucket's served names do not account for, column by
           column. With the index names served this is linking noise plus the
           cash bucket's benchmark override, well under the threshold below; it
           is the missing names themselves when a backend serves only the held
           ones. */
        const rest = {
          allocation: benchKnown ? b.allocation - sum('allocation') : null,
          selection: benchKnown ? b.selection - sum('selection') : null,
          active: b.total - sum('active'),
          benchWeight: benchKnown ? Math.max(0, b.benchWeight - sum('benchWeight')) : null,
        };
        /* With the benchmark names served individually this is only linking
           noise; it is material when they were not (a backend or a stored
           window that predates them). */
        const material = Math.abs(rest.active) >= 0.005 || rest.benchWeight >= 0.0005;
        return {
          ...b,
          /** The bucket's held stocks, biggest contributor against the index first. */
          holdings: held,
          /** The not-held remainder, so the bucket's stocks add up to it; null when there is none. */
          notHeld: material ? { symbol: NOT_HELD, weight: 0, ...rest } : null,
        };
      })
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

    /* A monthly period returning more than ±50% is not a market move, it is a
       bad row. Compounding through one silently produces a headline in the
       thousands of percent, so they are flagged rather than absorbed. */
    const OUTLIER_PCT = 50;
    const suspect = totals.filter(
      (r) => Math.abs(Number(r.portReturn) || 0) > OUTLIER_PCT
        || Math.abs(Number(r.benchReturn) || 0) > OUTLIER_PCT,
    );

    /* Returns compound across periods; effects add. Both arrive as percentages. */
    const compound = (pick) =>
      (totals.reduce((acc, r) => acc * (1 + (Number(pick(r)) || 0) / 100), 1) - 1) * 100;
    const portReturn = compound((r) => r.portReturn);
    const benchReturn = compound((r) => r.benchReturn);
    const overall = {
      portReturn,
      benchReturn,
      /* Active is the difference of the compounded paths, not a sum of actives. */
      activeReturn: portReturn - benchReturn,
      allocationEffect: totals.reduce((t, r) => t + (Number(r.allocationEffect) || 0), 0),
      selectionEffect: totals.reduce((t, r) => t + (Number(r.selectionEffect) || 0), 0),
    };

    /**
     * Per-security contribution, summed across periods.
     *
     * The same response that buckets the effects by sector also carries the
     * stock-level drill-down, so the contributors panel is a roll-up of this
     * rather than a second call. Contributions add across periods the way the
     * effects do — it is the returns that compound, not the contributions.
     *
     * Only the symbol comes back here; the caller joins it to a name.
     */
    const bySecurity = new Map();
    rows.forEach((r) => {
      (r.securities ?? EMPTY).forEach((s) => {
        /* An older run's summary row is the index, not a position; the fund-level
           not-held figure is built from the buckets' remainders instead. */
        if (!s?.securitySymbol || s.securitySymbol === NOT_HELD) return;
        const cur = bySecurity.get(s.securitySymbol)
          || { symbol: s.securitySymbol, name: null, held: false, value: 0, active: 0, selection: 0, weight: 0, benchWeight: 0 };
        if (s.securityName) cur.name = s.securityName;
        if (s.held !== false) cur.held = true;
        /* `value` is the plain contribution (weight × return); `active` is the
           stock's contribution AGAINST the index (`totalContribution`), which is
           what makes an underweight in a rallying stock a detractor. */
        cur.value += Number(s.contribution) || 0;
        cur.active += (Number(s.allocationContribution) || 0) + (Number(s.selectionContribution) || 0);
        cur.selection += Number(s.selectionContribution) || 0;
        /* Weights are point-in-time shares, so the latest period's stand. */
        cur.weight = Number(s.portWeight) || cur.weight;
        cur.benchWeight = Number(s.benchWeight) || cur.benchWeight;
        bySecurity.set(s.securitySymbol, cur);
      });
    });
    const securities = [...bySecurity.values()].sort((a, b) => b.value - a.value);

    return {
      loading: loading || groupLoading,
      error,
      available: Boolean(res?.success),
      message: res?.message,
      benchmarkName: res?.benchmarkName,
      /** What each holding contributed, biggest helper first. */
      securities,
      /** What the served names leave unexplained, summed across buckets: its active contribution, or null when nothing material. */
      notHeld: buckets.some((b) => b.notHeld)
        ? { active: buckets.reduce((t, b) => t + (b.notHeld?.active || 0), 0) }
        : null,
      maxContribution: Math.max(0.0001, ...securities.map((s) => Math.abs(s.value))),
      periods: totals.length,
      /* A latest-only read is one day, so that day is its window. */
      startDate: latest || res?.startDate,
      endDate: latest || res?.endDate,
      frequency: res?.frequency ?? frequency,
      /** Whether per-stock benchmark weights arrived — without them the stock-level split is not shown. */
      benchKnown,
      /** The bucketing group this ran against — what Fund Analysis is handed to repeat it. */
      groupId,
      /** Periods whose return is implausible for the frequency. */
      suspect,
      reliable: suspect.length === 0,
      buckets,
      overall,
      maxEffect: Math.max(0.0001, ...buckets.map((b) => Math.abs(b.total))),
    };
  }, [res, loading, groupLoading, error, latestOnly, frequency, groupId]);
}

/** Every bucketing a report can be cut by — "Major Sectors", "Style Factors", … */
export function useSecurityGroups() {
  const { data, loading, error, refetch } = useQuery(GET_SECURITY_GROUPS, { fetchPolicy: 'cache-first' });
  const groups = data?.getSecurityGroups ?? EMPTY;
  /* `refetch` is exposed because these rows can be created from a screen — the
     runner seeds the defaults when an environment has none, and the picker has
     to show them without a reload. */
  return useMemo(
    () => ({ loading, error, refetch, groups: groups.filter((g) => g.isActive !== false) }),
    [groups, loading, error, refetch],
  );
}

/**
 * The benchmark register, for a picker that has to send a symbol.
 *
 * Rows carrying no `securitySymbol` are dropped: the attribution API is given a
 * symbol, not a benchmark id, so a row without one cannot be run against and
 * offering it would only produce a failed run. Needs a signed-in session, and
 * answers 401 without one — hence `errorPolicy: 'all'`, so an unauthenticated
 * session gets an empty list and the caller's fallback rather than a crash.
 */
export function useBenchmarks() {
  const { data, loading, error } = useQuery(GET_BENCHMARKS, {
    variables: { isActive: true, securityGroupId: null },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const rows = data?.getBenchmarks ?? EMPTY;
  return useMemo(
    () => ({
      loading,
      error,
      benchmarks: rows.filter((b) => b.securitySymbol),
    }),
    [rows, loading, error],
  );
}

/** The horizons `getBrinsonAttribution` accepts. Any other name is rejected by the resolver. */
export const ATTRIBUTION_HORIZONS = [
  ['1m', '1 month'],
  ['1y', '1 year'],
  ['3y', '3 years'],
  ['5y', '5 years'],
  ['inception', 'Since inception'],
];

/* Stored rows carry a window, not the horizon they were cut for. Label one from
   its span so a row can be re-run on the same horizon it was first run on. */
const HORIZON_DAYS = [['1m', 31], ['1y', 366], ['3y', 1097], ['5y', 1828]];
function horizonForWindow(fromDate, toDate) {
  if (!fromDate || !toDate) return 'inception';
  const days = (new Date(toDate) - new Date(fromDate)) / 86400000;
  if (!Number.isFinite(days)) return 'inception';
  const hit = HORIZON_DAYS.find(([, max]) => days <= max + 15);
  return hit ? hit[0] : 'inception';
}

/**
 * One `BrinsonAttributionResponse` as the screens want it.
 *
 * Fractions become percentages here and nowhere else — see the units note on
 * BRINSON_ATTRIBUTION. Group-level returns already arrive as percentages,
 * so only the weights are scaled on that level.
 */
function brinsonView(res) {
  const pctOf = (v) => (Number(v) || 0) * 100;
  const runs = (res?.stored ?? EMPTY)
    .map((r) => ({
      id: r.id,
      dimension: r.dimension || `Group ${r.securityGroupId}`,
      securityGroupId: r.securityGroupId,
      benchmarkSymbol: r.benchmarkSymbol,
      from: r.fromDate,
      to: r.toDate,
      horizon: horizonForWindow(r.fromDate, r.toDate),
      computedAt: r.computedAt,
      full: Boolean(r.fullBrinson),
      portReturn: pctOf(r.portfolioReturn),
      benchReturn: pctOf(r.benchmarkReturn),
      activeReturn: pctOf(r.activeReturn),
      allocation: pctOf(r.totals?.allocationEffect),
      selection: pctOf(r.totals?.selectionEffect),
      interaction: pctOf(r.totals?.interactionEffect),
      total: pctOf(r.totals?.totalActiveReturn),
      groups: (r.groups ?? EMPTY)
        .map((g) => ({
          label: g.group || 'Unclassified',
          portWeight: pctOf(g.portfolioWeight),
          benchWeight: pctOf(g.benchmarkWeight),
          activeWeight: pctOf(g.activeWeight),
          /* Already percentages at this level — do not scale. */
          portReturn: Number(g.portfolioReturn) || 0,
          benchReturn: Number(g.benchmarkReturn) || 0,
          allocation: pctOf(g.allocationEffect),
          selection: pctOf(g.selectionEffect),
          interaction: pctOf(g.interactionEffect),
          total: pctOf(g.totalEffect),
        }))
        .sort((a, b) => Math.abs(b.total) - Math.abs(a.total)),
    }))
    .sort((a, b) => String(b.computedAt || '').localeCompare(String(a.computedAt || '')));

  const pending = (res?.pending ?? EMPTY).map((p) => ({
    key: `${p.horizon}:${p.securityGroupId}`,
    horizon: p.horizon,
    securityGroupId: p.securityGroupId,
    dimension: p.dimension || `Group ${p.securityGroupId}`,
    from: p.fromDate,
    to: p.toDate,
  }));

  return {
    available: Boolean(res?.success),
    status: res?.status ?? null,
    message: res?.message ?? null,
    runs,
    pending,
  };
}

/**
 * The stored attribution reports for one subject.
 *
 * Every row the engine has computed for the chosen horizons and bucketings,
 * newest first, plus the windows it could compute but has not (`pending`).
 */
export function useAttributionReports({
  portfolioSymbol, benchmarkSymbol, horizons, securityGroupIds,
} = {}) {
  const { asOf } = useSelector((s) => s.ui);
  const { data, loading, error, refetch } = useQuery(BRINSON_ATTRIBUTION, {
    variables: {
      portfolioSymbol,
      benchmarkSymbol: benchmarkSymbol || null,
      horizons: horizons?.length ? horizons : null,
      securityGroupIds: securityGroupIds?.length ? securityGroupIds : null,
      asOfDate: asOf || null,
    },
    skip: !portfolioSymbol,
    /* A run stores rows the cache cannot know about, so this must hit the
       network after every compute — refetch() alone would not if the cache
       were authoritative. */
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const res = data?.getBrinsonAttribution ?? null;
  return useMemo(
    () => ({ loading, error, refetch, ...brinsonView(res) }),
    [res, loading, error, refetch],
  );
}

/**
 * The most recent stored run, whatever window it was cut for.
 *
 * `getBrinsonAttribution` only returns rows whose window matches the horizons
 * asked for as of today, so a report computed a fortnight ago drops out of it
 * entirely — and the screen would claim nothing has ever been run. This is the
 * backstop that keeps that from happening, and what makes a stale report
 * visible enough to be worth re-running.
 */
export function useLatestAttributionReport({ portfolioSymbol, benchmarkSymbol } = {}) {
  const { data, loading, error, refetch } = useQuery(LATEST_BRINSON_ATTRIBUTION, {
    variables: { portfolioSymbol, benchmarkSymbol: benchmarkSymbol || null },
    skip: !portfolioSymbol,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const res = data?.getLatestBrinsonAttribution ?? null;
  return useMemo(
    () => ({ loading, error, refetch, ...brinsonView(res) }),
    [res, loading, error, refetch],
  );
}

/** Days of history the factor window covers when the caller names no dates. */
const MULTIFACTOR_WINDOW_DAYS = 30;

const isoDay = (date) => date.toISOString().slice(0, 10);

/**
 * Fund-level multifactor attribution over a window.
 *
 * The window ends on the app's as-of date and runs back a month, which is what
 * the admin console defaults to. `success: false` is an answer, not a failure —
 * the usual one is that the compute has not been run for these dates — so the
 * message is carried through for the panel to print rather than thrown.
 *
 * Every figure is already a percentage. Nothing downstream scales it.
 */
export function useMultifactorAttribution(securitySymbol, {
  skip = false, startDate: fixedStart, endDate: fixedEnd, latest = false,
} = {}) {
  const { asOf } = useSelector((s) => s.ui);
  const [startDate, endDate] = useMemo(() => {
    /* No dates at all is the resolver's own "latest stored day" — the exact
       answer to a daily read, with no guessing which day that was. */
    if (latest) return [null, null];
    if (fixedStart) return [fixedStart, fixedEnd || null];
    const end = asOf ? new Date(asOf) : new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - MULTIFACTOR_WINDOW_DAYS);
    return [isoDay(start), isoDay(end)];
  }, [asOf, latest, fixedStart, fixedEnd]);

  const { data, loading, error, refetch } = useQuery(MF_MULTIFACTOR_ATTRIBUTION, {
    variables: { securitySymbol, startDate, endDate },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const res = data?.getMfMultifactorAttribution ?? null;
  return useMemo(() => {
    const factors = [...(res?.factors ?? EMPTY)].sort(
      /* Biggest absolute effect first: the factor that moved the fund is the
         one the reader is looking for, whichever way it moved. */
      (a, b) => Math.abs(Number(b.factorEffect) || 0) - Math.abs(Number(a.factorEffect) || 0),
    );
    return {
      loading,
      error,
      /** Re-read the window — after a compute has stored the days it was missing. */
      refetch,
      /* The window the run actually linked, which for a latest-day read is only
         known once it answers. */
      startDate: res?.startDate ?? startDate,
      endDate: res?.endDate ?? endDate,
      available: Boolean(res?.success) && factors.length > 0,
      message: res?.message ?? null,
      benchmarkSymbol: res?.benchmarkSymbol ?? null,
      daysLinked: res?.daysLinked ?? null,
      activeReturn: res?.activeReturn ?? null,
      benchmarkReturn: res?.benchmarkReturn ?? null,
      factorTotal: res?.factorTotal ?? null,
      residualReturn: res?.residualReturn ?? null,
      factors,
      /**
       * How many factors the run actually priced.
       *
       * A stored run whose stocks have no factor scores comes back with every
       * cell null and a factorTotal of 0, which renders as a full table of
       * dashes reconciling to nothing. That is a gap in the scores, not a
       * broken panel, and the screen says which it is.
       */
      measured: factors.filter((f) => f.factorEffect != null).length,
      holdings: res?.holdings ?? EMPTY,
    };
  }, [res, loading, error, refetch, startDate, endDate]);
}

/**
 * The same factor attribution for a portfolio.
 *
 * `getMultifactorAttribution` is its own resolver — it keys on a portfolio
 * symbol rather than a scheme — but answers the identical shape, so this maps
 * it into exactly the object `useMultifactorAttribution` returns and the same
 * panel renders either without knowing which it was given.
 */
export function usePortfolioMultifactorAttribution(portfolioSymbol, {
  skip = false, startDate = null, endDate = null,
} = {}) {
  const { data, loading, error, refetch } = useQuery(PORTFOLIO_MULTIFACTOR_ATTRIBUTION, {
    variables: { portfolioSymbol, startDate, endDate },
    skip: skip || !portfolioSymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const res = data?.getMultifactorAttribution ?? null;
  return useMemo(() => {
    const factors = [...(res?.factors ?? EMPTY)].sort(
      (a, b) => Math.abs(Number(b.factorEffect) || 0) - Math.abs(Number(a.factorEffect) || 0),
    );
    return {
      loading,
      error,
      refetch,
      startDate: res?.startDate ?? startDate,
      endDate: res?.endDate ?? endDate,
      available: Boolean(res?.success) && factors.length > 0,
      message: res?.message ?? null,
      benchmarkSymbol: res?.benchmarkSymbol ?? null,
      daysLinked: res?.daysLinked ?? null,
      activeReturn: res?.activeReturn ?? null,
      benchmarkReturn: res?.benchmarkReturn ?? null,
      factorTotal: res?.factorTotal ?? null,
      residualReturn: res?.residualReturn ?? null,
      factors,
      measured: factors.filter((f) => f.factorEffect != null).length,
      holdings: res?.holdings ?? EMPTY,
    };
  }, [res, loading, error, refetch, startDate, endDate]);
}

/** More periods than this and the view asks the reader to narrow the window. */
export const MAX_FACTOR_PERIODS = 36;

/**
 * Factor attribution cut into periods — one stored, linked window per period.
 *
 * `getMfMultifactorAttribution` answers a single window per call, so a monthly
 * view over a year is twelve reads. They go out together through the client
 * (hooks cannot be called in a loop) and come back as `{ key, label, start,
 * end, res }` in period order; a period with nothing stored carries its
 * resolver message rather than failing the rest. Reads are cache-first, and
 * `refetch` re-reads every period from the network — what a compute that has
 * just stored the missing days needs.
 */
export function useMultifactorByPeriod(securitySymbol, { startDate, endDate, frequency = 'monthly', skip = false } = {}) {
  const client = useApolloClient();
  const windows = useMemo(() => periodWindows(startDate, endDate, frequency), [startDate, endDate, frequency]);
  const [state, setState] = useState({ loading: false, periods: EMPTY });
  const [nonce, setNonce] = useState(0);
  const tooMany = windows.length > MAX_FACTOR_PERIODS;

  useEffect(() => {
    if (skip || !securitySymbol || !windows.length || tooMany) {
      setState({ loading: false, periods: EMPTY });
      return undefined;
    }
    let cancelled = false;
    setState((st) => ({ ...st, loading: true }));
    Promise.all(windows.map((w) => client
      .query({
        query: MF_MULTIFACTOR_ATTRIBUTION,
        variables: { securitySymbol, startDate: w.start, endDate: w.end },
        fetchPolicy: nonce ? 'network-only' : 'cache-first',
        errorPolicy: 'all',
      })
      .then(({ data }) => ({ ...w, res: data?.getMfMultifactorAttribution ?? null }))
      .catch((e) => ({ ...w, res: { success: false, message: e.message } }))))
      .then((periods) => { if (!cancelled) setState({ loading: false, periods }); });
    return () => { cancelled = true; };
  }, [client, securitySymbol, windows, skip, tooMany, nonce]);

  return {
    ...state,
    windowCount: windows.length,
    tooMany,
    refetch: () => setNonce((n) => n + 1),
  };
}

/** The desk's saved report definitions. Unauthenticated sessions get `denied`. */
export function useAttributionSettings() {
  const { data, loading, error } = useQuery(ATTRIBUTION_SETTINGS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const res = data?.getAllAttributionSettings ?? null;
  return useMemo(() => ({
    loading,
    error,
    available: Boolean(res?.success),
    message: res?.message ?? null,
    denied: res?.success === false && /auth/i.test(res?.message || ''),
    settings: res?.settings ?? EMPTY,
  }), [res, loading, error]);
}

/**
 * What-if engine behind Proposals and the Analysis Studio: prices a set of
 * trades against the live portfolio and returns the tax and exposure impact.
 */
export function useScenario({ portfolioSymbol, trades, benchmarkSymbol }) {
  const { data, loading, error } = useQuery(ANALYZE_SCENARIO, {
    variables: { portfolioSymbol, trades: trades ?? [], benchmarkSymbol: benchmarkSymbol ?? null },
    skip: !portfolioSymbol || !trades?.length,
    fetchPolicy: 'network-only',
  });
  return { loading, error, scenario: data?.analyzeScenario ?? null };
}

/**
 * Behavioural traits for a portfolio. The resolver needs the investor's horizon
 * and risk band alongside the symbol; both default to the middle of each scale,
 * which is what the admin console's behavioural page also does. The values are
 * the backend's own verdict-grid keys (BehavioralHorizon / BehavioralRiskLevel)
 * — anything else misses the grid and every verdict reads "neutral".
 */
export function useBehavioralRisk(securitySymbol, { userHorizon = '3y', userRisk = 'moderate' } = {}) {
  const { data, loading, error } = useQuery(BEHAVIORAL_RISK, {
    variables: { securitySymbol, userHorizon, userRisk },
    skip: !securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const behavioral = data?.getBehavioralRisk ?? null;
  return {
    loading,
    error,
    behavioral,
    /* Traits worth surfacing first: the high-risk ones, then anything negative.
       The Health card and the client's Action Items both read this list. */
    highlights: (behavioral?.biases ?? EMPTY)
      .filter((b) => /HIGH|ELEVATED/i.test(b.level || '') || b.isPositiveTrait === false)
      .slice(0, 4),
  };
}

/** Whether a trait reads as a problem for this investor — a high level, or a
    "bad" verdict for their horizon and risk band. Drives the red/green mark. */
export const behaviourIsConcern = (b) =>
  /HIGH|ELEVATED/i.test(b?.level || '') || /bad/i.test(b?.verdict || '');

/** Human description of a scenario: the shock, the window, or the sigma move. */
function describeShock(sc) {
  if (!sc) return null;
  if (sc.isHistorical && sc.historicalStartDate) {
    return `Replays ${sc.historicalStartDate} → ${sc.historicalEndDate || 'today'}`;
  }
  if (sc.isStandardDeviation && sc.numberOfStandardDeviations != null) {
    return `${sc.numberOfStandardDeviations}σ move`;
  }
  const down = sc.shockValueNegativePercent;
  const up = sc.shockValuePositivePercent;
  if (down || up) {
    return [down ? `${down}%` : null, up ? `+${up}%` : null].filter(Boolean).join(' / ');
  }
  return sc.description || null;
}

/**
 * The scenarios configured in the system, keyed by name so a portfolio's
 * per-scenario risk row can be shown with the shock it represents.
 */
export function useStressScenarios() {
  const stress = useQuery(STRESS_SCENARIOS, { fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const sensitivity = useQuery(SENSITIVITY_SCENARIOS, { fetchPolicy: 'cache-first', errorPolicy: 'all' });

  return useMemo(() => {
    const stressRows = stress.data?.allRiskStressScenarios ?? EMPTY;
    const sensitivityRows = sensitivity.data?.allRiskSensitivityScenarios ?? EMPTY;
    const byName = new Map();
    [...stressRows, ...sensitivityRows].forEach((sc) => {
      if (sc?.scenarioName) byName.set(String(sc.scenarioName).toLowerCase(), sc);
    });
    return {
      loading: stress.loading || sensitivity.loading,
      error: stress.error || sensitivity.error,
      stress: stressRows,
      sensitivity: sensitivityRows,
      byName,
      describe: (name) => describeShock(byName.get(String(name || '').toLowerCase())),
    };
  }, [stress, sensitivity]);
}

/**
 * Which desk owns the simulation configurations. The backend partitions them by
 * an opaque tag rather than by user id, and this is the one the platform's rows
 * are stored under — the same constant the admin console sends.
 */
const RISK_SIMULATION_USER_TAG = 'KeepInvesting_Dev';

/**
 * The scenarios a risk run can actually be executed under, as one list.
 *
 * Two catalogues feed it. A **stress scenario** says what happens to the market
 * — a historical window replayed, a shock, a sigma move. A **simulation
 * configuration** says how the Monte Carlo itself is run — window, half-life,
 * path count. `computePortfolioRisk` takes either in the same argument, so
 * offering them as two controls would only ask the reader to know which
 * catalogue a name came from.
 *
 * Rows without a `scenarioValue` are dropped: the slug is what the engine is
 * given, so a row without one cannot be run and listing it would only produce a
 * failed run. Inactive rows are dropped for the same reason.
 */
export function useRiskScenarios() {
  const stress = useQuery(STRESS_SCENARIOS, { fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const sims = useQuery(RISK_SIMULATION_CONFIGURATIONS, {
    variables: { userTag: RISK_SIMULATION_USER_TAG },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const runnable = (rows) => (rows ?? EMPTY).filter((r) => r.scenarioValue && r.isActive !== false);

    const stressRows = runnable(stress.data?.allRiskStressScenarios).map((s) => ({
      value: s.scenarioValue,
      label: s.scenarioName || s.scenarioValue,
      kind: 'Stress scenario',
      description: s.description || null,
      detail: describeShock(s),
    }));

    const simRows = runnable(sims.data?.riskSimulationConfigurationsByUser).map((c) => ({
      value: c.scenarioValue,
      label: c.scenarioName || c.scenarioValue,
      kind: 'Simulation config',
      description: c.description || null,
      detail: [
        c.numberOfSimulations ? `${num(c.numberOfSimulations)} paths` : null,
        c.historicWindowSize ? `${c.historicWindowSize}d window` : null,
        c.isExponential && c.halfLifeDays ? `${c.halfLifeDays}d half-life` : null,
      ].filter(Boolean).join(' · ') || null,
    }));

    return {
      loading: stress.loading || sims.loading,
      error: stress.error || sims.error,
      stress: stressRows,
      simulations: simRows,
      /* Simulation configs first: one of them is the engine's own default. */
      scenarios: [...simRows, ...stressRows],
    };
  }, [stress, sims]);
}
